import { useMemo, type ReactNode } from "react";
import {
  AsyncTaskProvider,
  useAsyncTasks,
  type AsyncTaskHandlerConfig,
  type AsyncTaskHandlerValue,
  type TaskEntry,
  type TaskLifecycle,
  type TaskEvent,
} from "@/modern/contexts/async-task-handler";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";

// ── Domain Types ────────────────────────────────────────────────────────────

export type TaskPhase = "idle" | "analysing" | "building" | "suggesting";
export type { TaskEntry, TaskLifecycle, TaskEvent };

export type BoardContextTaskType = "questions" | "context" | "suggestion";

export interface ContextTaskState {
  questions: TaskEntry | null;
  context: TaskEntry | null;
  suggestion: TaskEntry | null;
}

// ── Board-Context Config ────────────────────────────────────────────────────

function makeBoardContextConfig(
  boardId: number | undefined
): AsyncTaskHandlerConfig<BoardContextTaskType, TaskPhase> {
  return {
    taskTypeMap: {
      [ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION]: "questions",
      [ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION]: "context",
      [ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION]: "suggestion",
    },
    filterTask: (task) =>
      [
        ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
        ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
        ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION,
      ].includes(task.type) &&
      Number(task.meta_data?.board_id) === Number(boardId),
    getGroupKey: (task) => {
      const id = Number(task.meta_data?.board_context_id ?? 0);
      return id || null;
    },
    derivePhase: (state): TaskPhase => {
      if (state.context?.status === "pending") return "building";
      if (state.questions?.status === "pending") return "analysing";
      if (state.suggestion?.status === "pending") return "suggesting";
      return "idle";
    },
  };
}

// ── Provider ────────────────────────────────────────────────────────────────

interface AsyncTaskHandlerProviderProps {
  boardId: number | undefined;
  children: ReactNode;
}

export const AsyncTaskHandlerProvider = ({
  boardId,
  children,
}: AsyncTaskHandlerProviderProps) => {
  const config = useMemo(() => makeBoardContextConfig(boardId), [boardId]);

  return (
    <AsyncTaskProvider config={config}>
      {children}
    </AsyncTaskProvider>
  );
};

// ── Hook ────────────────────────────────────────────────────────────────────

type AsyncTaskHandlerHookValue = AsyncTaskHandlerValue<BoardContextTaskType, TaskPhase>;

export const useAsyncTaskHandler = (): AsyncTaskHandlerHookValue => {
  return useAsyncTasks<BoardContextTaskType, TaskPhase>();
};
